/** Escolhe o renderizador pelo FORMATO do livro (contrato 03↔04↔10).
 *
 * É a costura que o `design.md` da Fase 03 previu: o shell não sabe se o livro
 * é PDF ou HTML5, e o `/access` é format-aware. Separar isto do layout é o que
 * permitiu ao PDF apoiar-se só no shell (correção C2 do plano MVP).
 */
import { PDFContentArea } from '@/features/reader/components/PDFContentArea'
import type { PdfLoader, TextLayerRenderer } from '@/features/reader/hooks/usePdfDocument'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import type { BookAccess, Highlight } from '@/types/api'

interface ReaderContentProps {
  access: BookAccess
  studentName: string
  loader?: PdfLoader
  /** Porta de teste, como o `loader`: sem ela, um teste que passe por aqui
   *  importa o pdf.js de verdade e a suíte fica lenta e barulhenta. */
  textLayerRenderer?: TextLayerRenderer
  onFalhaDeCarregamento?: (erro: unknown) => void
  renovando?: boolean
  destaques?: Highlight[]
  onCriarDestaque?: React.ComponentProps<typeof PDFContentArea>['onCriarDestaque']
}

export function ReaderContent({
  access,
  studentName,
  loader,
  textLayerRenderer,
  onFalhaDeCarregamento,
  renovando,
  destaques,
  onCriarDestaque,
}: ReaderContentProps) {
  const page = useReaderStore((s) => s.page)
  const irParaPagina = useReaderStore((s) => s.irParaPagina)

  if (access.format === 'pdf') {
    return (
      <PDFContentArea
        access={access}
        page={page}
        onPageChange={(p) => irParaPagina(p, access.page_count ?? undefined)}
        studentName={studentName}
        {...(loader ? { loader } : {})}
        {...(textLayerRenderer ? { textLayerRenderer } : {})}
        {...(onFalhaDeCarregamento ? { onFalhaDeCarregamento } : {})}
        {...(renovando !== undefined ? { renovando } : {})}
        {...(destaques ? { destaques } : {})}
        {...(onCriarDestaque ? { onCriarDestaque } : {})}
      />
    )
  }

  // O leitor HTML5 roda em origem sandbox dedicada (ADR-001) e está fora da
  // fatia PDF — ver docs/specs/03-leitor/tasks.md TASK-05.
  return <p role="status">Este livro está em HTML5. O leitor de HTML5 chega na próxima etapa.</p>
}
